import { useId } from 'react';
import { OptionSelect } from '@/components/form/field';
import { SegmentedControl } from '@/components/lumen/segmented-control';
import { cn } from '@/lib/utils';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { useTextScale, type TextScale } from '@contexts/TextScaleContext';
import { colors as palettes, SELECTABLE_THEME_MODES, type ThemeMode } from '@constants/colors';
import { LANGUAGES, type LanguageCode } from '@constants/translations';

/** Same names the sidebar footer uses for the theme toggle. */
const THEME_LABEL_KEY: Partial<Record<ThemeMode, string>> = {
  apex: 'theme.apex',
  'apex-night': 'theme.apexNight',
  horizon: 'theme.horizon',
  'horizon-night': 'theme.horizonNight',
  dawn: 'theme.horizonDawn',
  dusk: 'theme.horizonDusk',
  mist: 'theme.horizonMist',
};

const TEXT_SCALE_OPTIONS: { value: TextScale; labelKey: string; pct: string }[] = [
  { value: 1, labelKey: 'settings.textSizeNormal', pct: '100%' },
  { value: 1.1, labelKey: 'settings.textSizeLarge', pct: '110%' },
  { value: 1.2, labelKey: 'settings.textSizeLarger', pct: '120%' },
];

export interface TampilanControlsProps {
  /** Renders the language control on its own row under the swatches (narrow hosts). */
  stacked?: boolean;
  /**
   * Kept for the hosts that still pass it. The Lumen select layers itself, so
   * it is ignored; removed with the last Mantine modal in PR 10.
   */
  dropdownZIndex?: number;
}

/**
 * Theme, language and text size, the three personal display settings.
 *
 * One component for the two places that offer them, the Pengaturan page and
 * the first-login wizard, so the controls and their copy cannot drift.
 */
export default function TampilanControls({ stacked = false }: TampilanControlsProps) {
  const { mode: themeMode, toggleTheme } = useTheme();
  const { t, language, setLanguage } = useLanguage();
  const { scale: textScale, setScale: setTextScale } = useTextScale();
  const languageId = useId();

  const themeSwatches = (
    <div className="flex flex-col gap-2.5">
      <p className="font-semibold text-fg">{t('settings.theme')}</p>
      <p className="text-fg-muted text-sm">{t('settings.themeDesc')}</p>
      <div className="mt-1 flex flex-wrap items-center gap-3">
        {SELECTABLE_THEME_MODES.map((option) => {
          const palette = (palettes as Record<string, Record<string, string | undefined>>)[option] ?? {};
          const selected = option === themeMode;
          return (
            <button
              key={option}
              type="button"
              onClick={() => toggleTheme(option)}
              aria-pressed={selected}
              aria-label={t(THEME_LABEL_KEY[option] ?? 'sidebar.theme')}
              className={cn(
                'w-26 cursor-pointer overflow-hidden rounded-control border-[1.5px] bg-raised p-0 outline-none focus-visible:ring-[3px] focus-visible:ring-label/40',
                selected ? 'border-label ring-2 ring-label/20' : 'border-line'
              )}
            >
              {/* The three stripes show the swatch's own theme, so they stay inline (data-driven colours). */}
              <div className="grid h-9 grid-cols-3">
                <span style={{ backgroundColor: palette.sidebar ?? palette.background }} />
                <span style={{ backgroundColor: palette.accent }} />
                <span style={{ backgroundColor: palette.background }} />
              </div>
              <div
                className={cn(
                  'px-1.5 py-1 text-center text-xs',
                  selected ? 'font-semibold text-fg' : 'font-medium text-fg-muted'
                )}
              >
                {t(THEME_LABEL_KEY[option] ?? 'sidebar.theme')}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const languageSelect = (
    <OptionSelect
      id={languageId}
      className="w-[200px] shrink-0"
      value={language}
      onChange={(value) => {
        if (value) setLanguage(value as LanguageCode);
      }}
      options={LANGUAGES.map((lang) => ({ value: lang.code, label: `${lang.flag} ${lang.label}` }))}
      aria-label={t('settings.language')}
    />
  );

  const languageRow = stacked ? (
    <div className="flex flex-col items-start gap-2.5">
      <p className="font-semibold text-fg">{t('settings.language')}</p>
      <p className="text-fg-muted text-sm">{t('settings.languageDesc')}</p>
      {languageSelect}
    </div>
  ) : (
    <div className="flex flex-nowrap items-start justify-between gap-4">
      <div className="flex-1">
        <p className="font-semibold text-fg">{t('settings.language')}</p>
        <p className="text-fg-muted text-sm">{t('settings.languageDesc')}</p>
      </div>
      {languageSelect}
    </div>
  );

  const textSize = (
    <div className="flex flex-col gap-2.5">
      <p className="font-semibold text-fg">{t('settings.textSize')}</p>
      <p className="text-fg-muted text-sm">{t('settings.textSizeDesc')}</p>
      <SegmentedControl
        value={String(textScale)}
        onChange={(value) => {
          const next = Number(value);
          if (next === 1 || next === 1.1 || next === 1.2) setTextScale(next);
        }}
        options={TEXT_SCALE_OPTIONS.map((option) => ({
          value: String(option.value),
          label: `${t(option.labelKey)} (${option.pct})`,
        }))}
        aria-label={t('settings.textSize')}
      />
      <div className="rounded-card border border-line border-dashed px-3 py-2.5 text-fg-muted">
        <p className="text-fg text-sm">{t('settings.textSizePreview')}</p>
        <p className="text-fg-muted text-xs">{t('settings.textSizeNote')}</p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      {themeSwatches}
      {languageRow}
      {textSize}
    </div>
  );
}
